import { ColumnDef } from "@tanstack/react-table";
import { Constants } from "appConstants";
import BackofficeApplicationRegistryFilterForm, {
	ApplicationRegistryFilters
} from "components/BackofficeApplicationRegistryFilterForm";
import BackofficeList, { FilterTools } from "components/BackofficeList";
import {
	ApplicationRegistryStatusDisplay,
	asCellRenderer,
	BasicValueDisplay,
	LocaleDateShortValueDisplay
} from "components/BackofficeList/modules/ValueDisplays";
import TooltipWrapper from "components/TooltipWrapper";
import { ErrorHelper, TableHelper } from "helpers";
import LocalStorageHelper from "helpers/LocalStorageHelper";
import usePagination from "hooks/usePagination";
import useStabilizedState from "hooks/useStabilizedState";
import useUrlFilters from "hooks/useUrlFilters";
import { AnalysisStatusCode } from "models/types";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { ApplicationService } from "services/applicationService";
import { ApplicationFilterOptions } from "services/types";

export type ApplicationRegistryItem = {
	id: string;
	applicationProcessCode: string;
	issuedAt: string; // Data em que a solicitação foi originalmente aberta; formato UTC
	concessionaireName: string;
	proprietorName: string;
	interventionTypeName: string;
	highwayCode: string;
	currentUserResponsibleGroup: Constants.UserRoleGroups;
	currentAnalysisStatus: AnalysisStatusCode; // sempre a mais atual
	lastAnalysisEndDatetime: string | null; // a última análise que possuir endDatetime; yyyy-MM-dd
};

const emptyFilters: ApplicationRegistryFilters = {
	from: "",
	to: "",
	concessionaires: [],
	interventionTypes: [],
	lastAnalysisStatuses: [],
	responsibleGroups: []
};

const headerClassName =
	"flex flex-row items-center justify-start border-0 text-xs font-semi text-neutral-low-pure-500 h-12 p-2";
const cellClassName =
	"flex flex-row items-center justify-start border-0 text-sm text-neutral-low-400 h-12 p-2";

type ApplicationRegistryItemColumn = ColumnDef<
	ApplicationRegistryItem,
	ApplicationRegistryItem[keyof ApplicationRegistryItem]
>;

const applicationRegistryColumns: ApplicationRegistryItemColumn[] =
	TableHelper.addClassNames(
		[
			{
				accessorKey: "applicationProcessCode",
				header: "Nº do processo",
				enableSorting: false,
				cell: asCellRenderer(BasicValueDisplay),
				size: 7
			},
			{
				accessorKey: "issuedAt",
				header: "Data de criação",
				enableSorting: false,
				cell: asCellRenderer<ApplicationRegistryItem, "issuedAt">(
					LocaleDateShortValueDisplay
				),
				size: 6,
				meta: { headerClass: "justify-center", cellClass: "justify-center" }
			},
			{
				accessorKey: "lastAnalysisEndDatetime",
				header: () => (
					<div
						id="lastAnalysisEndDatetimeHeader"
						className="w-full h-full flex flex-row justify-center items-center"
					>
						<Tooltip anchorSelect="#lastAnalysisEndDatetimeHeader" place="top">
							<div className="text-base font-normal max-w-[96vw] md:max-w-[30rem] text-wrap">
								Referente ao último protocolo respondido
							</div>
						</Tooltip>
						<span>Data da resposta</span>
					</div>
				),
				enableSorting: false,
				cell: asCellRenderer<
					ApplicationRegistryItem,
					"lastAnalysisEndDatetime"
				>(LocaleDateShortValueDisplay),
				size: 6
			},
			{
				accessorKey: "proprietorName",
				header: "Solicitante",
				enableSorting: false,
				cell: asCellRenderer(({ value }) => (
					<TooltipWrapper place="top" value={value?.toUpperCase()} />
				)),
				size: 10
			},
			{
				accessorKey: "currentUserResponsibleGroup",
				header: "Área",
				enableSorting: false,
				cell: asCellRenderer(({ value }) => (
					<BasicValueDisplay
						value={Constants.getReadableGroupName(
							value as Constants.UserRoleGroups
						)}
					/>
				)),
				size: 5
			},
			{
				accessorKey: "currentAnalysisStatus",
				header: "Status",
				enableSorting: false,
				cell: asCellRenderer(ApplicationRegistryStatusDisplay),
				size: 8
			},
			{
				accessorKey: "concessionaireName",
				header: "Concessionária",
				enableSorting: false,
				cell: asCellRenderer(BasicValueDisplay),
				size: 7
			},
			{
				accessorKey: "interventionTypeName",
				header: "Tipo de intervenção",
				enableSorting: false,
				cell: asCellRenderer(BasicValueDisplay),
				size: 11
			}
		],
		cellClassName,
		headerClassName
	);

export default function BackofficeApplicationRegistry(): JSX.Element {
	const [urlParams, setUrlParams] = useSearchParams();

	const paginationTools = usePagination(
		LocalStorageHelper.backofficeRegistryPageSizeKey
	);
	const { stablePage, pageItemCount, setPageCount, setPage } = paginationTools;

	const [storedSearchText, setStoredSearchText] =
		LocalStorageHelper.useLocalStorageState<string>(
			LocalStorageHelper.backofficeRegistrySearchTextKey
		);

	const {
		setValue: setSearchText,
		stableValue: stableSearchText,
		unstableValue: searchText
	} = useStabilizedState<string>(
		urlParams.get("search") ?? storedSearchText ?? "",
		750
	);

	const searchTools = {
		setSearchText: (newSearchText: string) => {
			setSearchText(newSearchText);
			setStoredSearchText(newSearchText);
			setPage(Constants.INITIAL_PAGE_INDEX);
		},
		stableSearchText,
		searchText
	};

	const [filters, setFilters] = useUrlFilters(
		emptyFilters,
		urlParams,
		(newParams) => {
			setPage(Constants.INITIAL_PAGE_INDEX);
			setUrlParams(newParams);
		},
		ApplicationService.parseBackofficeRegistryFilterParams,
		ApplicationService.makeBackofficeApplicationFilterParams,
		LocalStorageHelper.backofficeRegistryFiltersKey
	);
	const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);

	const filterTools: FilterTools<ApplicationRegistryFilters> = {
		filters,
		initialFilters: emptyFilters,
		resetFilters: () => {
			setFilters(emptyFilters);
		},
		setFilters,
		setModalOpen: setIsFilterModalOpen
	};

	const applicationService = ApplicationService.getInstance();

	const [items, setItems] = useState<ApplicationRegistryItem[] | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const [filterOptions, setFilterOptions] =
		useState<ApplicationFilterOptions | null>(null);
	const [isLoadingFilterOptions, setIsLoadingFilterOptions] =
		useState<boolean>(false);

	useEffect(() => {
		const newParams = new URLSearchParams(urlParams);
		newParams.set("search", stableSearchText);

		setUrlParams(newParams);
	}, [stableSearchText, urlParams]);

	const loadData = useCallback(() => {
		setIsLoading(true);
		applicationService
			.getAllApplicationsList(
				stablePage - 1,
				pageItemCount,
				stableSearchText,
				filters
			)
			.then((responseData) => {
				setPageCount(responseData.totalPages);
				setItems(responseData.content);
			})
			.catch((err) => ErrorHelper.showErrorMessage(err))
			.finally(() => setIsLoading(false));
	}, [
		stablePage,
		pageItemCount,
		stableSearchText,
		filters,
		setItems,
		setPageCount,
		setIsLoading
	]);

	useEffect(() => {
		loadData();
	}, [loadData]);

	return (
		<div className="h-full">
			<BackofficeList
				columns={applicationRegistryColumns}
				items={items ?? []}
				loading={isLoading}
				title="Consulta"
				paginationTools={paginationTools}
				searchTools={searchTools}
				filterTools={filterTools}
			/>
			{isFilterModalOpen && (
				<BackofficeApplicationRegistryFilterForm
					setFilters={setFilters}
					clearFilters={() => {
						setFilters(emptyFilters);
						setSearchText("");
					}}
					initialFilters={filters}
					isOpen={isFilterModalOpen}
					setIsOpen={setIsFilterModalOpen}
					filterOptions={filterOptions}
					setFilterOptions={setFilterOptions}
					isLoadingFilterOptions={isLoadingFilterOptions}
					setIsLoadingFilterOptions={setIsLoadingFilterOptions}
				/>
			)}
		</div>
	);
}
